import { Attachments } from '@EHSNavigator/backend';
import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';
import Button from '../Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import { Document, Page, pdfjs } from 'react-pdf';
import ReactPlayer from 'react-player';
import ArrowRight from '../../assets/icons/arrow-right.svg?react';
import DocxPreview from './DocPreview';
import { useMutation } from '@tanstack/react-query';
import useAuthenticatedAxios from '../../client/api/useAuthenticatedAxios';

interface AttachmentPreviewModalProps {
  otherClasses?: string;
  handleClose: () => void;
  attachment: Attachments | null;
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
  otherClasses,
  handleClose,
  attachment,
}) => {
  const attachmentPreviewModalClasses = clsx(otherClasses, 'flex flex-col h-full max-h-[80vh]');
  const axios = useAuthenticatedAxios();
  const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const file: any = React.useMemo(
    () => ({ url: attachment?.url }),
    [attachment?.url]
  );

  useEffect(() => {
    const updatePageWidth = () => {
      if (pdfContainerRef.current) {
        const containerWidth = pdfContainerRef.current.offsetWidth;
        setPageWidth(containerWidth);
      }
    };

    const timer = setTimeout(updatePageWidth, 100);
    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePageWidth);
    };
  }, [attachment]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setLoading(false);
  }

  function onDocumentLoadStart() {
    setLoading(true);
  }

  const downloadMutation = useMutation({
    mutationFn: async (attachment: Attachments) => {
      if (!attachment?.url || !attachment?.name) {
        throw new Error('Invalid attachment data');
      }

      const downloadFileDto = {
        url: attachment.url || '',
        name: attachment.name || '',
        fileType: attachment.mediaType || 'application/octet-stream',
      };
      
      const response = await axios.post(
        `${BASE_URL}api/file/download`,
        downloadFileDto,
        {
          responseType: 'blob',
        },
      );
      return { blob: response.data, fileName: attachment.name || '' };
    },
    onSuccess: (data) => {
      const blob = new Blob([data.blob]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = data.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Download failed:', error);
    },
  });

  const handleDownload = () => {
    if (attachment) {
      downloadMutation.mutate(attachment);
    }
  };


  return (
    <div
      className={attachmentPreviewModalClasses}
      data-testid="attachment-preview-modal"
    >
      <div className="bg-brand-byzantine font-inter-variable -tracking-button sticky top-0 z-10 flex cursor-pointer items-center justify-center gap-2.5 p-2.5 text-base font-bold text-white">
        Preview Attachments
      </div>

      <div
        className={clsx(
          '-overflow-y-auto flex-1  p-4 global-scrollbar-style overflow-x-hidden',
          attachment?.mediaType === 'application/msword'
            ? 'overflow-y-hidden'
            : '',
        )}
      >
        {attachment?.mediaType?.startsWith('image/') ? (
          <img
            src={attachment?.url}
            alt={attachment?.name}
            loading="eager"
            className="w-full rounded-lg"
          />
        ) : attachment?.mediaType?.startsWith('video/') ? (
          <ReactPlayer
            style={{
              width: "100%",
              height: "100%",
            }}
            playsInline
            preload='metadata'
            className="min-h-380 w-full rounded-lg"
            src={attachment?.url}
            controls
          />
        ) : attachment?.mediaType ===
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ? (
          <DocxPreview url={attachment?.url || ''} />
        ) : attachment?.mediaType === 'application/msword' ? (
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
              attachment?.url || '',
            )}`}
            className="h-[629px] max-h-[629px] max-w-full w-full overflow-x-hidden"
          />
        ) : (
          <div ref={pdfContainerRef} className="relative w-full max-w-full overflow-x-hidden">
            <div className="w-full max-w-full overflow-x-hidden">
              <Document
                file={file}
                loading={
                  <div className="flex h-500 items-center justify-center ">
                    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-400"></div>
                  </div>
                }
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadStart={onDocumentLoadStart}
                className="w-full"
              >
                <Page 
                  pageNumber={pageNumber} 
                  width={pageWidth} 
                  className="max-w-full w-full"
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
            </div>

            <p className="font-inter-variable mt-4 text-center text-lg">
              Page <strong className="text-semibold">{pageNumber}</strong> of{' '}
              <strong className="text-semibold">{numPages}</strong>
            </p>
            {numPages > 1 && !loading && (
              <div className="absolute top-2/4 left-0 z-20 flex w-full -translate-y-2/4 items-center justify-between">
                <button
                  className="cursor-pointer"
                  onClick={() =>
                    setPageNumber(pageNumber === 1 ? 1 : pageNumber - 1)
                  }
                >
                  <ArrowRight className="rotate-180" />
                </button>
                <button
                  className="cursor-pointer"
                  onClick={() =>
                    setPageNumber(
                      pageNumber === numPages ? numPages : pageNumber + 1,
                    )
                  }
                >
                  <ArrowRight />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-neutral-tones-text/10 sticky bottom-0 z-10 flex flex-col-reverse gap-15 border-t p-4 md:grid md:grid-cols-2 md:gap-6">
        <Button
          variant="secondary"
          type="button"
          size={useButtonSizeSmall()}
          onClick={() => handleClose()}
          otherClasses={clsx(
            'text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink',
          )}
        >
          Cancel
        </Button>
        <Button
          size={useButtonSizeSmall()}
          onClick={handleDownload}
          loading={downloadMutation.isPending}
          otherClasses={clsx(
            'bg-brand-papery hover:text-brand-papery hover:border-brand-papery',
          )}
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default AttachmentPreviewModal;
